import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from './Tooltip';

/**
 * Text that ellipsises, with the app's own tooltip carrying the full string —
 * **and only when it is actually cut off**.
 *
 * Replaces `title={...}` on truncating labels. A native title is slow to
 * appear, cannot be styled, and never shows on touch; it is also unconditional,
 * so hovering a name that is fully visible pops a tooltip repeating what is
 * already on screen.
 *
 * Truncation is measured (`scrollWidth > clientWidth`) rather than guessed from
 * length: the column is a fixed width in px and the text is proportional, so
 * character count says nothing about whether it fits. It is re-measured on
 * resize and whenever the text changes, because a pane the user drags narrower
 * truncates names that fitted a moment ago.
 */
export default function TruncatedText({ text, className, side = 'top', ...rest }) {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // +1: sub-pixel layout can leave scrollWidth a hair over clientWidth on
    // text that is not actually clipped, which would tooltip every row.
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') {
      // jsdom and older webviews: fall back to the window event. Being wrong
      // here costs a tooltip that does not appear, never a broken row.
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [measure, text]);

  return (
    <Tooltip content={text} side={side} disabled={!truncated}>
      <span ref={ref} className={className} {...rest}>
        {text}
      </span>
    </Tooltip>
  );
}
